import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios, { API } from '../../services/api';
import {
  MagnifyingGlass, X, Spinner, CaretRight, Check, Plus, PencilSimple
} from '@phosphor-icons/react';

const SEVERITY_OPTIONS = [
  { key: 'mild', label: 'Mild', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { key: 'moderate', label: 'Moderate', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'severe', label: 'Severe', color: 'bg-red-100 text-red-700 border-red-200' },
];

const STATUS_OPTIONS = [
  { key: 'diagnosed', label: 'Diagnosed' },
  { key: 'suspected', label: 'Suspected' },
];

// P2 Stage-14b — 10 common-condition chips so a brand-new user is never
// staring at a bare search box. Tap to add (severity = "moderate", status =
// "diagnosed" by default — user can edit after). All ten are common across
// our Australian audience and span mental-health / metabolic / women's
// health / chronic so the chip strip doesn't read as a single specialty.
const COMMON_CONDITION_CHIPS = [
  { name: 'Anxiety',         category: 'mental_health' },
  { name: 'Depression',      category: 'mental_health' },
  { name: 'ADHD',            category: 'mental_health' },
  { name: 'Migraine',        category: 'neurological' },
  { name: 'IBS',             category: 'gastrointestinal' },
  { name: 'PCOS',            category: 'reproductive' },
  { name: 'Endometriosis',   category: 'reproductive' },
  { name: 'Hypothyroidism',  category: 'endocrine' },
  { name: 'Hypertension',    category: 'cardiovascular' },
  { name: 'Type 2 diabetes', category: 'endocrine' },
];

export const OnboardingConditions = ({ data, onChange }) => {
  const { conditions = [], suspected_conditions = [] } = data;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuspected, setShowSuspected] = useState(suspected_conditions.length > 0);
  const [suspectedInput, setSuspectedInput] = useState('');
  const [suspectedNotes, setSuspectedNotes] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const searchTimeout = useRef(null);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data: results } = await axios.get(`${API}/conditions/search?q=${encodeURIComponent(query)}`);
        const existingNames = conditions.map(c => c.name.toLowerCase());
        setSearchResults(results.filter(r => !existingNames.includes(r.name.toLowerCase())));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 250);
  }, [conditions]);

  const addCondition = (result) => {
    const updated = [...conditions, {
      name: result.name,
      category: result.category,
      severity: 'moderate',
      status: 'diagnosed',
      notes: '',
      source: 'catalog',
    }];
    onChange({ ...data, conditions: updated });
    setSearchQuery('');
    setSearchResults([]);
  };

  const updateCondition = (index, field, value) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...data, conditions: updated });
  };

  const removeCondition = (index) => {
    setEditingIdx(null);
    onChange({ ...data, conditions: conditions.filter((_, i) => i !== index) });
  };

  const addCustomCondition = () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    const updated = [...conditions, {
      name: searchQuery.trim(),
      category: 'other',
      severity: 'moderate',
      status: 'diagnosed',
      notes: '',
      source: 'custom',
    }];
    onChange({ ...data, conditions: updated });
    setSearchQuery('');
    setSearchResults([]);
  };

  const addSuspected = () => {
    if (!suspectedInput.trim()) return;
    const updated = [...suspected_conditions, { name: suspectedInput.trim(), notes: suspectedNotes.trim() }];
    onChange({ ...data, suspected_conditions: updated });
    setSuspectedInput('');
    setSuspectedNotes('');
  };

  const removeSuspected = (index) => {
    onChange({ ...data, suspected_conditions: suspected_conditions.filter((_, i) => i !== index) });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">
          What health conditions are you currently managing?
        </p>
        <p className="text-sm text-slate-400 mt-2">
          These can be diagnosed conditions, or things you suspect but haven't confirmed.
        </p>
      </div>

      {/* P2 Stage-14b — Quick-add chips. Hidden once a few conditions have
          been added so the chip strip doesn't keep dominating the screen. */}
      {conditions.length < 3 && (
        <div data-testid="onboarding-condition-chips">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400 mb-2">
            Common — tap to add
          </p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_CONDITION_CHIPS
              .filter((c) => !conditions.some((existing) => existing.name.toLowerCase() === c.name.toLowerCase()))
              .map((chip) => (
                <button
                  key={chip.name}
                  type="button"
                  onClick={() => addCondition({ name: chip.name, category: chip.category })}
                  className="text-[12.5px] text-[#235347] bg-[#235347]/8 border border-[#235347]/15 rounded-full px-3 py-1.5 hover:bg-[#235347]/14 transition-colors flex items-center gap-1"
                  data-testid={`onboarding-condition-chip-${chip.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Plus size={11} weight="bold" />
                  {chip.name}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="onboarding-condition-search"
          placeholder="Or search for something else (e.g. asthma, lupus)"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-[#235347]/20 text-sm"
          autoFocus
        />
        {searching && <Spinner size={18} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>

      {/* Search results */}
      <AnimatePresence>
        {searchResults.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="border border-slate-200 rounded-xl overflow-hidden bg-white divide-y divide-slate-100 -mt-2">
            {searchResults.slice(0, 6).map((result) => (
              <button key={result.name} data-testid={`onboarding-condition-result-${result.name}`}
                onClick={() => addCondition(result)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{result.name}</p>
                  <p className="text-xs text-slate-400">{result.category_label}</p>
                </div>
                <CaretRight size={16} className="text-slate-300" />
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* No results — add custom */}
      {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
        <div className="text-center py-3">
          <p className="text-sm text-slate-400 mb-2">No match found.</p>
          <button onClick={addCustomCondition} className="text-sm text-[#235347] font-medium hover:underline">
            Add "{searchQuery}" as a custom condition
          </button>
        </div>
      )}

      {/* Selected conditions with inline edit */}
      <AnimatePresence>
        {conditions.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Added conditions</p>
            {conditions.map((cond, idx) => (
              <motion.div key={cond.name} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white border border-slate-200 rounded-xl p-4" data-testid="onboarding-condition-item">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-emerald-600" weight="bold" />
                    <span className="text-sm font-medium text-slate-900">{cond.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                      className="text-slate-300 hover:text-[#235347] p-1" data-testid={`edit-condition-${idx}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => removeCondition(idx)} className="text-slate-300 hover:text-red-500 p-1"
                      data-testid={`remove-condition-${idx}`}>
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Severity */}
                <div className="flex gap-2 mb-2">
                  {SEVERITY_OPTIONS.map(({ key, label, color }) => (
                    <button key={key} data-testid={`onboarding-severity-${cond.name}-${key}`}
                      onClick={() => updateCondition(idx, 'severity', key)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        cond.severity === key ? color : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Status */}
                <div className="flex gap-2">
                  {STATUS_OPTIONS.map(({ key, label }) => (
                    <button key={key} onClick={() => updateCondition(idx, 'status', key)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        cond.status === key ? 'bg-[#235347]/10 text-[#235347] border-[#235347]/30' : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Expanded edit area */}
                <AnimatePresence>
                  {editingIdx === idx && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <label className="text-[10px] text-slate-400 font-medium uppercase mb-1 block">Notes</label>
                        <input value={cond.notes || ''} onChange={(e) => updateCondition(idx, 'notes', e.target.value)}
                          placeholder="Any context — when diagnosed, triggers, etc."
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                          data-testid={`condition-notes-${idx}`} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Suspected conditions */}
      <div>
        {!showSuspected ? (
          <button onClick={() => setShowSuspected(true)}
            className="text-sm text-slate-500 hover:text-[#235347] transition-colors flex items-center gap-1">
            <Plus size={14} /> Add a suspected or unconfirmed condition
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Suspected / Unconfirmed</p>
            {suspected_conditions.map((sc, idx) => (
              <div key={idx} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-900">{sc.name}</p>
                  {sc.notes && <p className="text-xs text-amber-600">{sc.notes}</p>}
                </div>
                <button onClick={() => removeSuspected(idx)} className="text-amber-300 hover:text-red-500 p-1">
                  <X size={16} />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <input data-testid="onboarding-suspected-input" placeholder="Condition name" value={suspectedInput}
                onChange={(e) => setSuspectedInput(e.target.value)}
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none" />
              <input placeholder="Notes (optional)" value={suspectedNotes}
                onChange={(e) => setSuspectedNotes(e.target.value)}
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none" />
              <button onClick={addSuspected} disabled={!suspectedInput.trim()}
                className="px-4 py-2.5 bg-[#235347] text-white rounded-xl text-sm font-medium disabled:opacity-40">
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
